import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * StationTilesModal
 * Presenta una cuadrícula visual de tarjetas (tiles) organizadas por función/rol,
 * inspirada en el patrón de /admin/dashboard (Sections.js).
 * Permite a operadores no técnicos elegir su puesto de trabajo de manera inmediata.
 */
export const StationTilesModal = ({
    isOpen,
    onClose,
    currentStationCode,
    currentMode,
    onSelectStation,
    stations = [],
    zones = []
}) => {
    if (!isOpen) return null;

    // Configuración estática y visual de los grupos de estaciones
    const SECTIONS_CONFIG = [
        {
            title: 'Control de Puertas y Accesos',
            subtitle: 'Validación de boletos QR, control de reingresos y aforo de zonas',
            icon: 'bi bi-door-open-fill',
            color: '#10b981',
            mode: 'ACCESS',
            stations: [
                {
                    stationCode: 'P1_ACCESO',
                    stationName: 'Puerta 1 General',
                    detail: 'Check-In Principal • Explanada General',
                    icon: 'bi bi-person-walking',
                    badge: 'Anti-Passback'
                },
                {
                    stationCode: 'VIP_DOOR',
                    stationName: 'Acceso Hospitalidad VIP',
                    detail: 'Lounge Caliente • Reingreso Libre',
                    icon: 'bi bi-star-fill',
                    badge: 'VIP & Cancha'
                },
                {
                    stationCode: 'CANCHA_DOOR',
                    stationName: 'Túnel Acceso Cancha',
                    detail: 'Experiencia Nivel Cancha Xolos',
                    icon: 'bi bi-shield-shaded',
                    badge: 'Solo Cancha'
                }
            ]
        },
        {
            title: 'Puntos de Venta (Alimentos y Tienda)',
            subtitle: 'Registro de consumos, caja táctil y estampas para el pasaporte',
            icon: 'bi bi-shop',
            color: '#f59e0b',
            mode: 'POS_STORE',
            stations: [
                {
                    stationCode: 'STAND_XOLOSHOP',
                    stationName: 'Stand Oficial XoloShop',
                    detail: 'Gorras, jerseys y bufandas oficiales',
                    icon: 'bi bi-bag-check-fill',
                    badge: 'Merchandising'
                },
                {
                    stationCode: 'STAND_TACOS',
                    stationName: 'Tacos & Grill Perrón',
                    detail: 'Corredor gastronómico y bebidas',
                    icon: 'bi bi-fire',
                    badge: 'Gastronomía'
                }
            ]
        },
        {
            title: 'Activaciones de Patrocinador',
            subtitle: 'Dinámicas interactivas y sellado de dinámicas obligatorias',
            icon: 'bi bi-trophy-fill',
            color: '#dc2626',
            mode: 'ACTIVATION',
            stations: [
                {
                    stationCode: 'ACT_CALIENTE',
                    stationName: 'Tiro a Gol Caliente.mx',
                    detail: 'Dinámica de precisión y portería',
                    icon: 'bi bi-dribbble',
                    badge: 'Sello Obligatorio'
                },
                {
                    stationCode: 'ACT_NISSAN',
                    stationName: 'Reto Precisión Nissan',
                    detail: 'Simulador y test drive virtual',
                    icon: 'bi bi-car-front-fill',
                    badge: '100 Puntos'
                }
            ]
        },
        {
            title: 'Carpa de Premios y Souvenirs',
            subtitle: 'Validación del Cupón Dorado y entrega física de premios',
            icon: 'bi bi-gift-fill',
            color: '#eab308',
            mode: 'REWARD_REDEEM',
            stations: [
                {
                    stationCode: 'REWARD_BOOTH',
                    stationName: 'Módulo de Canje de Souvenirs',
                    detail: 'Entrega de Vaso Conmemorativo Oficial (#CANJE)',
                    icon: 'bi bi-award-fill',
                    badge: 'Validación Cupón'
                }
            ]
        }
    ];

    return (
        <AnimatePresence>
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.88)',
                    backdropFilter: 'blur(8px)',
                    zIndex: 1050,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px',
                    overflowY: 'auto'
                }}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 15 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 15 }}
                    transition={{ duration: 0.2 }}
                    style={{
                        background: '#121216',
                        border: '2px solid rgba(245, 158, 11, 0.5)',
                        borderRadius: 24,
                        width: '100%',
                        maxWidth: 960,
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        boxShadow: '0 25px 60px rgba(0, 0, 0, 0.85)'
                    }}
                >
                    {/* Encabezado del Selector */}
                    <div
                        style={{
                            padding: '20px 24px',
                            background: 'linear-gradient(180deg, #18181b 0%, #121216 100%)',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 12
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    background: 'rgba(245, 158, 11, 0.18)',
                                    border: '1px solid #f59e0b',
                                    color: '#fde68a',
                                    borderRadius: 999,
                                    padding: '3px 10px',
                                    fontSize: 11,
                                    fontWeight: 800,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.8,
                                    marginBottom: 6
                                }}
                            >
                                <i className="bi bi-geo-alt-fill"></i> Puestos de Trabajo
                            </div>
                            <h2
                                style={{
                                    margin: 0,
                                    fontSize: '1.45rem',
                                    fontWeight: 900,
                                    color: '#ffffff'
                                }}
                            >
                                ¿En qué estación estás operando hoy?
                            </h2>
                            <p
                                style={{
                                    margin: '4px 0 0',
                                    fontSize: '0.85rem',
                                    color: '#a1a1aa'
                                }}
                            >
                                Selecciona tu puesto para configurar la terminal con los controles exactos de tu tarea.
                            </p>
                        </div>

                        {onClose && (
                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.08)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    color: '#ffffff',
                                    borderRadius: 12,
                                    width: 40,
                                    height: 40,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: 18,
                                    fontWeight: 700
                                }}
                                title="Cerrar selector"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    {/* Contenido con Scroll de Secciones de Tiles */}
                    <div
                        style={{
                            padding: '20px 24px',
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 22
                        }}
                    >
                        {SECTIONS_CONFIG.map((sec, idx) => (
                            <div
                                key={sec.mode || idx}
                                style={{
                                    background: '#18181b',
                                    border: '1px solid rgba(255, 255, 255, 0.08)',
                                    borderRadius: 18,
                                    padding: '16px 18px'
                                }}
                            >
                                {/* Cabecera de Sección */}
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: 14,
                                        flexWrap: 'wrap',
                                        gap: 8
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <div
                                            style={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: 10,
                                                background: `${sec.color}22`,
                                                color: sec.color,
                                                border: `1px solid ${sec.color}55`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 18
                                            }}
                                        >
                                            <i className={sec.icon}></i>
                                        </div>
                                        <div>
                                            <h4
                                                style={{
                                                    margin: 0,
                                                    fontSize: '1rem',
                                                    fontWeight: 900,
                                                    color: '#ffffff'
                                                }}
                                            >
                                                {sec.title}
                                            </h4>
                                            <span style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>
                                                {sec.subtitle}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Rejilla de Tiles Táctiles (Patrón /admin/dashboard) */}
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                                        gap: 12
                                    }}
                                >
                                    {sec.stations.map((st) => {
                                        const isSelected =
                                            currentStationCode === st.stationCode &&
                                            currentMode === sec.mode;

                                        return (
                                            <div
                                                key={st.stationCode}
                                                onClick={() => {
                                                    onSelectStation(st.stationCode, sec.mode);
                                                    if (onClose) onClose();
                                                }}
                                                style={{
                                                    background: isSelected
                                                        ? `linear-gradient(145deg, ${sec.color}28 0%, #141418 100%)`
                                                        : '#0f0f12',
                                                    border: isSelected
                                                        ? `2.5px solid ${sec.color}`
                                                        : '1.5px solid rgba(255, 255, 255, 0.12)',
                                                    borderRadius: 14,
                                                    padding: '16px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'space-between',
                                                    minHeight: 120,
                                                    transition: 'all 0.15s ease',
                                                    boxShadow: isSelected
                                                        ? `0 8px 24px ${sec.color}33`
                                                        : 'none'
                                                }}
                                            >
                                                <div>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'flex-start',
                                                            marginBottom: 8
                                                        }}
                                                    >
                                                        <i
                                                            className={`${st.icon}`}
                                                            style={{
                                                                fontSize: 26,
                                                                color: isSelected ? sec.color : '#e4e4e7'
                                                            }}
                                                        ></i>

                                                        <span
                                                            style={{
                                                                fontSize: '0.68rem',
                                                                fontWeight: 800,
                                                                padding: '3px 8px',
                                                                borderRadius: 6,
                                                                background: isSelected
                                                                    ? `${sec.color}33`
                                                                    : 'rgba(255, 255, 255, 0.08)',
                                                                color: isSelected ? '#ffffff' : '#a1a1aa',
                                                                border: isSelected
                                                                    ? `1px solid ${sec.color}`
                                                                    : '1px solid transparent'
                                                                }}
                                                        >
                                                            {st.badge}
                                                        </span>
                                                    </div>

                                                    <h5
                                                        style={{
                                                            margin: '0 0 4px',
                                                            fontSize: '0.98rem',
                                                            fontWeight: 900,
                                                            color: isSelected ? '#ffffff' : '#f4f4f5'
                                                        }}
                                                    >
                                                        {st.stationName}
                                                    </h5>
                                                    <p
                                                        style={{
                                                            margin: 0,
                                                            fontSize: '0.74rem',
                                                            color: '#a1a1aa',
                                                            lineHeight: 1.3
                                                        }}
                                                    >
                                                        {st.detail}
                                                    </p>
                                                </div>

                                                {isSelected && (
                                                    <div
                                                        style={{
                                                            marginTop: 10,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 5,
                                                            fontSize: '0.75rem',
                                                            fontWeight: 800,
                                                            color: sec.color
                                                        }}
                                                    >
                                                        <i className="bi bi-check-circle-fill"></i> Puesto Actual
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pie del Selector con Botón Rápido */}
                    <div
                        style={{
                            padding: '14px 24px',
                            background: '#18181b',
                            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <span style={{ fontSize: '0.78rem', color: '#a1a1aa' }}>
                            <i className="bi bi-info-circle text-warning me-1"></i>
                            Puedes cambiar de puesto en cualquier momento con el botón superior.
                        </span>
                        {onClose && (
                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    background: '#f59e0b',
                                    color: '#09090b',
                                    border: 'none',
                                    borderRadius: 10,
                                    padding: '8px 16px',
                                    fontWeight: 900,
                                    fontSize: '0.85rem',
                                    cursor: 'pointer'
                                }}
                            >
                                Continuar
                            </button>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default StationTilesModal;
